import React, { useCallback, useEffect, useState, useRef } from 'react';
import { Plus, Pencil, Trash2, X, Upload, Loader2 } from 'lucide-react';
import { getEvents } from '../../lib/api';
import { adminCreateEvent, adminUpdateEvent, adminDeleteEvent, adminUpload } from '../../lib/adminApi';
import { useToast } from '../../hooks/use-toast';

const CATEGORIES = ['Worship', 'Community', 'Kids', 'Men', 'Women', 'Youth', 'Outreach'];
const EMPTY = { title: '', date: '', time: '', location: '', category: 'Worship', image: '', description: '', signupUrl: '' };
const input = 'w-full bg-white border border-[#d8d2c6] px-3 py-2.5 outline-none focus:border-[#17565f] transition-colors rounded';

const EventsManager = () => {
  const { toast } = useToast();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | id
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      setEvents(await getEvents());
    } catch (err) {
      console.error('Failed to load events:', err);
      toast({ title: 'Could not load events', description: 'Please refresh and try again.' });
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const openNew = () => { setForm(EMPTY); setEditing('new'); };
  const openEdit = (ev) => { setForm({ ...EMPTY, ...ev }); setEditing(ev.id); };
  const close = () => { setEditing(null); setForm(EMPTY); };
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const res = await adminUpload(file);
      setForm((f) => ({ ...f, image: res.fullUrl }));
      toast({ title: 'Image uploaded' });
    } catch (err) {
      toast({ title: 'Upload failed', description: err?.response?.data?.detail || 'Try a smaller image.' });
    } finally { setUploading(false); }
  };

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing === 'new') { await adminCreateEvent(form); toast({ title: 'Event created' }); }
      else { await adminUpdateEvent(editing, form); toast({ title: 'Event updated' }); }
      close();
      load();
    } catch (err) {
      toast({ title: 'Save failed', description: err?.response?.data?.detail || 'Please check the fields.' });
    } finally { setSaving(false); }
  };

  const remove = async (ev) => {
    if (!window.confirm(`Delete "${ev.title}"? This cannot be undone.`)) return;
    try { await adminDeleteEvent(ev.id); toast({ title: 'Event deleted' }); load(); }
    catch (err) { console.error('Delete event failed:', err); toast({ title: 'Delete failed' }); }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-8">
        <div>
          <h2 className="font-serif-display text-3xl text-[#0f2e38]">Events</h2>
          <p className="text-[#5a6b71] mt-1">Add, edit, or remove events. Changes appear on the site instantly.</p>
        </div>
        <button onClick={openNew} className="inline-flex items-center gap-2 bg-[#17565f] text-white hover:bg-[#0f2e38] px-5 py-3 rounded font-600 transition-colors">
          <Plus className="h-4 w-4" /> New Event
        </button>
      </div>

      {loading ? (
        <p className="text-[#5a6b71]">Loading…</p>
      ) : events.length === 0 ? (
        <p className="text-[#5a6b71]">No events yet. Click “New Event” to add one.</p>
      ) : (
        <div className="grid gap-4">
          {events.map((ev) => (
            <div key={ev.id} className="bg-white border border-[#e4ddd0] rounded-lg p-4 flex items-center gap-4">
              <div className="h-16 w-24 bg-[#efe9dd] rounded overflow-hidden shrink-0">
                {ev.image ? <img src={ev.image} alt={ev.title} className="h-full w-full object-cover" /> : null}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-600 text-[#0f2e38] truncate">{ev.title}</p>
                <p className="text-sm text-[#5a6b71]">{ev.date} · {ev.time} · {ev.category}</p>
              </div>
              <button onClick={() => openEdit(ev)} className="p-2 text-[#17565f] hover:bg-[#f0ebe0] rounded transition-colors"><Pencil className="h-4 w-4" /></button>
              <button onClick={() => remove(ev)} className="p-2 text-red-600 hover:bg-red-50 rounded transition-colors"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4 overflow-y-auto">
          <form onSubmit={save} className="bg-white rounded-xl w-full max-w-2xl my-8 max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-6 border-b border-[#e4ddd0] sticky top-0 bg-white">
              <h3 className="font-serif-display text-2xl text-[#0f2e38]">{editing === 'new' ? 'New Event' : 'Edit Event'}</h3>
              <button type="button" onClick={close} className="p-2 hover:bg-[#f0ebe0] rounded"><X className="h-5 w-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              <div><label className="block text-sm text-[#3a4d54] mb-1">Title</label><input required value={form.title} onChange={set('title')} className={input} /></div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-sm text-[#3a4d54] mb-1">Date</label><input required type="date" value={form.date} onChange={set('date')} className={input} /></div>
                <div><label className="block text-sm text-[#3a4d54] mb-1">Time</label><input required value={form.time} onChange={set('time')} placeholder="10:30 AM" className={input} /></div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div><label className="block text-sm text-[#3a4d54] mb-1">Location</label><input required value={form.location} onChange={set('location')} className={input} /></div>
                <div><label className="block text-sm text-[#3a4d54] mb-1">Category</label>
                  <select value={form.category} onChange={set('category')} className={input}>
                    {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>
              <div><label className="block text-sm text-[#3a4d54] mb-1">Description</label><textarea rows={3} value={form.description} onChange={set('description')} className={input} /></div>
              <div><label className="block text-sm text-[#3a4d54] mb-1">Sign-up / RSVP link (optional)</label><input value={form.signupUrl} onChange={set('signupUrl')} placeholder="https://…" className={input} /></div>
              <div>
                <label className="block text-sm text-[#3a4d54] mb-1">Photo</label>
                <div className="flex items-center gap-4">
                  <div className="h-20 w-28 bg-[#efe9dd] rounded overflow-hidden shrink-0">
                    {form.image ? <img src={form.image} alt="preview" className="h-full w-full object-cover" /> : null}
                  </div>
                  <div>
                    <input ref={fileRef} type="file" accept="image/*" onChange={handleUpload} className="hidden" />
                    <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="inline-flex items-center gap-2 border border-[#d8d2c6] hover:border-[#17565f] px-4 py-2 rounded text-sm transition-colors">
                      {uploading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Upload Photo
                    </button>
                    <p className="text-xs text-[#8a9599] mt-2">JPG or PNG, up to 6MB.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-end gap-3 p-6 border-t border-[#e4ddd0] sticky bottom-0 bg-white">
              <button type="button" onClick={close} className="px-5 py-2.5 border border-[#d8d2c6] rounded hover:bg-[#f0ebe0] transition-colors">Cancel</button>
              <button type="submit" disabled={saving} className="inline-flex items-center gap-2 bg-[#c9a24b] text-[#0b2027] hover:bg-[#d8b566] px-6 py-2.5 rounded font-700 transition-colors">
                {saving && <Loader2 className="h-4 w-4 animate-spin" />} Save
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
};

export default EventsManager;
